import React from 'react';

import {
  dashboardDeliveryRequestLogApis,
  deliveryRequestLogApis,
} from '@apis/next/deliveryRequestLogApis';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { getChangeLogDefaultDetail } from '@data/changeLog';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { useSearch } from '@hooks/next/useSearch';
import { type DeliveryRequestLogFormatters } from '@models/next/DeliveryRequestLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDate, formatDateTime, toJST } from '@utils/types';
import clsx from 'clsx';
import { max } from 'lodash-es';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    title: `${deliveryRequestStrings.changeLog} - ${deliveryRequestStrings.deliveryRequestFile}`,
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
  },
});

export interface PrintDeliveryRequestLogProps {
  type: 'admin' | 'external';
}

export const PrintDeliveryRequestLog: React.FC<PrintDeliveryRequestLogProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { type } = props;

  const {
    search: { id, delivery_request_log_access },
  } = useSearch(
    z
      .object({
        id: z.number(),
        delivery_request_log_access: z.string(),
      })
      .partial(),
  );

  const deliveryRequestLogQuery =
    type === 'admin'
      ? dashboardDeliveryRequestLogApis.useGet('/dashboard/delivery-request-log/:id', {
          params: { id: id! },
        })
      : deliveryRequestLogApis.useGet('/delivery-request-log', {
          queries: { delivery_request_log_access: delivery_request_log_access! },
        });

  const formatters: DeliveryRequestLogFormatters = {
    pickup_date: (v) => formatDate(v) ?? '-',
    delivery_date: (v) => formatDate(v) ?? '-',
    delivery_date_type: (v) => (v === 'TIME' ? deliveryRequestStrings.deliveryDateTypeTime : v),
  };

  const bodyColumns =
    deliveryRequestLogQuery.data?.map((item, index) => {
      // Bug in typescript
      const formatter = (formatters[item.edited_field] ?? String) as typeof String;

      return [
        cellFactory.create((index + 1).toString()),
        cellFactory.create(item.editor || '-'),
        cellFactory.create(formatDateTime(item.updated_at)),
        cellFactory.create(item.label || '-'),
        cellFactory.create(formatter(item.before_value) || '-'),
        cellFactory.create(formatter(item.after_value) || '-'),
        cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
      ];
    }) ?? [];

  let contents: React.ReactNode = null;
  const head = (
    <Table.Head>
      <Table.Row
        columns={[
          cellFactory.create(strings.no || '-'),
          cellFactory.create(strings.editor || '-'),
          cellFactory.create(strings.updatedAt || '-'),
          cellFactory.create(strings.editedField || '-'),
          cellFactory.create(strings.beforeValue || '-'),
          cellFactory.create(strings.afterValue || '-'),
          cellFactory.create(strings.detail || '-'),
        ]}
      />
    </Table.Head>
  );

  if (deliveryRequestLogQuery.data && deliveryRequestLogQuery.data?.length > 0) {
    contents = (
      <Table>
        {head}
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (deliveryRequestLogQuery.data && deliveryRequestLogQuery.data?.length === 0) {
    contents = (
      <>
        <Table>{head}</Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt =
    deliveryRequestLogQuery.data && max(deliveryRequestLogQuery.data.map((d) => d.updated_at));

  const pageContent = (
    <PrintableContainer classes={{ inner: classes.customInner }}>
      {updatedAt && (
        <p className={clsx(classes.horizontalSpace1, classes.middleText)}>
          <span>Latest Update:</span>
          <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
        </p>
      )}
      {contents}
    </PrintableContainer>
  );

  if (type === 'external') {
    return <QueryContainer queries={[deliveryRequestLogQuery]}>{pageContent}</QueryContainer>;
  }

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={deliveryRequestLogQuery.isLoading}
      title={strings.title}
      errors={[deliveryRequestLogQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      {pageContent}
    </Layout>
  );
};
